import { useMemo } from "react"
import { Link } from "react-router-dom"
import { formatMoney } from "@/lib/format"
import { useSettlementSummary } from "@/lib/settlements"
import type { RangeSpec } from "./api"
import { rangeDates, ymd } from "./time"

/**
 * Analytics home: "Settled (Authorize.net)" and "Unsettled" for the picked range (Mo, 3 Nov).
 * Terify counts an order the moment it's paid (store day); Authorize.net's sales figure only counts
 * batches it has settled, and payments after the daily cut-off stay unsettled until the next batch.
 * Settled = Terify's card payments in batches settled on these days (net of refunds); Unsettled =
 * Terify's card payments taken on these days still waiting for a batch. The merchant account is
 * shared with other storefronts: their part is shown on its own line, never mixed in.
 */
export function SettlementOverview({ range, tz, refresh, className = "mb-4" }: { range: RangeSpec; tz: string; refresh: boolean; className?: string }) {
  const { since, until } = useMemo(() => {
    const d = rangeDates(range, tz)
    return { since: ymd(d.from), until: ymd(d.to) }
  }, [range, tz])
  const q = useSettlementSummary(since, until, refresh ? 60_000 : false)
  const s = q.data
  if (q.isError || (s && !s.status.connected)) return null
  const money = (n: number | undefined) => (n == null ? "—" : formatMoney(n, "usd"))
  const other = s ? s.settled.other.net : 0
  const synced = s?.status.last_ok_at ? new Date(s.status.last_ok_at) : null
  return (
    <section aria-label="Card payments (Authorize.net)" className={`${className} rounded-xl bg-white p-4 shadow-[var(--tf-card-shadow)]`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <Link to={`/analytics/settlements?since=${since}&until=${until}`} className="text-[13px] font-semibold underline decoration-dotted underline-offset-4">
          Card payments (Authorize.net)
        </Link>
        <span className="text-[12px] text-ui-fg-subtle">
          {s?.environment === "sandbox" ? "Sandbox · " : ""}
          {synced ? `Synced ${synced.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : s ? "Not synced yet" : ""}
          {" · "}
          <Link to={`/analytics/settlements?since=${since}&until=${until}`} className="hover:underline">Settlements</Link>
        </span>
      </div>
      {s?.status.setup_required ? (
        <p role="status" className="mb-3 rounded-lg bg-[#fff8db] px-3 py-2 text-[12px] text-[#4f3a00]">{s.status.setup_message}</p>
      ) : s?.status.last_error ? (
        <p role="status" className="mb-3 rounded-lg bg-[#fee9e8] px-3 py-2 text-[12px] text-[#8e1f0b]">Last sync failed: {s.status.last_error}</p>
      ) : null}
      <div className={`grid grid-cols-2 gap-x-4 gap-y-3 ${q.isFetching && !q.isLoading ? "opacity-80" : ""}`}>
        <div className="min-w-0">
          <div className="truncate text-[12px] text-ui-fg-subtle" title="Keel's card payments in batches Authorize.net settled on these days, minus refunds that settled. This is what the processor deposits.">
            Settled (Authorize.net)
          </div>
          <div className="k-num text-[20px] font-medium" data-testid="settled-net">{money(s?.settled.terify.net)}</div>
          <div className="text-[12px] text-ui-fg-subtle">
            {s ? `${s.settled.account.batches} batch${s.settled.account.batches === 1 ? "" : "es"}${s.settled.terify.refunds ? ` · ${money(s.settled.terify.refunds)} refunds` : ""}` : " "}
          </div>
        </div>
        <div className="min-w-0">
          <div className="truncate text-[12px] text-ui-fg-subtle" title="Keel's card payments taken on these days that Authorize.net hasn't settled yet. Payments after the daily cut-off settle in the next batch.">
            Unsettled
          </div>
          <div className="k-num text-[20px] font-medium" data-testid="unsettled-net">{money(s?.unsettled.terify.net)}</div>
          <div className="text-[12px] text-ui-fg-subtle">
            {s
              ? [
                  "Pending settlement",
                  s.unsettled.authorized.terify ? `${money(s.unsettled.authorized.terify)} authorized, not captured` : null,
                  s.unsettled_now.terify.net !== s.unsettled.terify.net ? `${money(s.unsettled_now.terify.net)} unsettled in all` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : " "}
          </div>
        </div>
      </div>
      {s && (other !== 0 || s.unsettled.other.net !== 0) && (
        <p className="mt-3 border-t border-[#ebebeb] pt-3 text-[12px] text-ui-fg-subtle">
          The Authorize.net account also settled {money(other)} from other storefronts in these batches (account total {money(s.settled.account.net)})
          {s.unsettled.other.net ? `, and ${money(s.unsettled.other.net)} of theirs is unsettled` : ""}. Those aren't Keel orders and aren't counted above.
        </p>
      )}
    </section>
  )
}
